import type { ReactNode } from "react";
import { useLocation } from "@tanstack/react-router";
import { buildHref } from "@softmax/web-addresses";
import { useQuery } from "@tanstack/react-query";
import { useBrowserSession } from "@softmax/web-features/session";
import { useActualViewer } from "@softmax/web-features/session/records";
import { siteFlagsQueryOptions } from "@/features/site-shell/records";
import { flagIsOn, observatoryFlags } from "@softmax/web-features/flags";
import {
  SiteViewerProvider,
  CornersProvider,
  SiteFooter,
} from "@softmax/web-features/site";
import {
  SiteAccountPill,
  FooterSlot,
  BrokenImageGuard,
} from "@/features/site-shell";
import { MainSiteAnalyticsContext } from "@softmax/web-features/telemetry";
import { publicNavigationOptions } from "@softmax/web-features/site/queries";
import { defaultBrowseLinks } from "@softmax/web-features/site/records";
import { AccountEntry } from "@/features/site-shell/navigation";
import { FeedbackBootstrap } from "@softmax/web-features/feedback";
import { CogentPanel } from "@/features/play/cogent";

export function SiteFrame({
  children,
  agent = true,
}: {
  children: ReactNode;
  /** A not-found page has no game for the agent to read. */
  agent?: boolean;
}) {
  const { session } = useBrowserSession();
  const actual = useActualViewer();
  const flags = useQuery(siteFlagsQueryOptions());
  const launched = flags.data
    ? flagIsOn({
        flags: flags.data.flags,
        key: observatoryFlags.websiteTopnav,
      })
    : null;
  const onGetStarted =
    useLocation({ select: (location) => location.pathname }) ===
    buildHref({ name: "getStarted" });
  const navigation = useQuery(publicNavigationOptions());
  const browseItems = navigation.data ?? defaultBrowseLinks;
  const viewer = actual.data
    ? {
        name: actual.data.name ?? "",
        image:
          session?.user.id === actual.data.subject_id
            ? (session.user.image ?? null)
            : null,
      }
    : null;
  return (
    <SiteViewerProvider
      viewerId={actual.data?.subject_id ?? null}
      signedIn={Boolean(actual.data)}
    >
      <MainSiteAnalyticsContext />
      <CornersProvider>
        <SiteAccountPill viewer={viewer} leagues={browseItems} />

        {children}
        <FooterSlot>
          <SiteFooter browseItems={browseItems} />
        </FooterSlot>
        {/* On Get Started the agent is the page, centered. */}
        {agent && launched === true && !onGetStarted ? <CogentPanel /> : null}
        <BrokenImageGuard />
        <AccountEntry />
        <FeedbackBootstrap />
      </CornersProvider>
    </SiteViewerProvider>
  );
}
